/**
 * Envoi du formulaire de contact au service contact-us.
 *
 * Le formulaire appelle une URL relative (par défaut `/api/contact`) : le nginx de
 * barinta.org relaie cet appel vers le service contact-us. L'appel reste donc sur
 * le même domaine que le site, sans CORS. Le service reconnaît le site grâce à
 * l'en-tête `Origin` (ou `Referer`) envoyé par le navigateur.
 */

export const DEFAULT_CONTACT_API_URL = '/api/contact';

export const CONTACT_API_URL = process.env.REACT_APP_CONTACT_API_URL || DEFAULT_CONTACT_API_URL;

// Clé publique reCAPTCHA v3 (vérification invisible, sans case à cocher).
export const DEFAULT_RECAPTCHA_SITE_KEY = '6LdLy9ctAAAAACLvVk8-75pQUczM4hrY66egthGR';

export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || DEFAULT_RECAPTCHA_SITE_KEY;

// Nom d'action transmis à reCAPTCHA v3 lors de l'envoi du formulaire.
export const RECAPTCHA_ACTION = 'contact';

export const CONTACT_EMAIL = 'association@barinta.org';

// Limites alignées sur la validation du service contact-us.
export const LIMITS = {
    namePart: 120,
    name: 241,
    phone: 40,
    messageMin: 10,
    messageMax: 4000
};

const REQUEST_TIMEOUT_MS = 20000;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FALLBACK_ERROR = "Votre message n'a pas pu être envoyé. Réessayez plus tard ou écrivez-nous à "
    + CONTACT_EMAIL + '.';

/**
 * Découpe « Nom complet » en prénom (premier mot) et nom (le reste), comme le
 * fait le service pour un champ `name` unique.
 */
export function splitName(fullName) {
    const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return {firstName: '', lastName: ''};
    }
    return {firstName: parts[0], lastName: parts.slice(1).join(' ')};
}

/**
 * Vérifie les champs avant l'envoi. Retourne un objet `{champ: message}`, vide si
 * le formulaire est valide.
 */
export function validateContact(values) {
    const errors = {};
    const name = (values.name || '').trim();
    const email = (values.email || '').trim();
    const phone = (values.phone || '').trim();
    const message = (values.message || '').trim();

    if (!name) {
        errors.name = 'Indiquez votre nom.';
    } else {
        const {firstName, lastName} = splitName(name);
        if (firstName.length > LIMITS.namePart || lastName.length > LIMITS.namePart) {
            errors.name = 'Le nom est trop long.';
        }
    }

    if (!email) {
        errors.email = 'Indiquez votre adresse email.';
    } else if (!EMAIL_PATTERN.test(email)) {
        errors.email = 'Indiquez une adresse email valide.';
    }

    if (phone.length > LIMITS.phone) {
        errors.phone = `Le numéro de téléphone ne doit pas dépasser ${LIMITS.phone} caractères.`;
    }

    if (!message) {
        errors.message = 'Écrivez votre message.';
    } else if (message.length < LIMITS.messageMin) {
        errors.message = `Le message doit contenir au moins ${LIMITS.messageMin} caractères.`;
    } else if (message.length > LIMITS.messageMax) {
        errors.message = `Le message ne doit pas dépasser ${LIMITS.messageMax} caractères.`;
    }

    return errors;
}

/**
 * Construit le corps JSON attendu par `POST /api/contact`.
 * Les champs facultatifs vides ne sont pas envoyés.
 */
export function buildPayload(values, captchaToken) {
    const {firstName, lastName} = splitName(values.name);
    const payload = {
        first_name: firstName,
        email: (values.email || '').trim(),
        message: (values.message || '').trim(),
        captcha: captchaToken
    };
    if (lastName) {
        payload.last_name = lastName;
    }
    const phone = (values.phone || '').trim();
    if (phone) {
        payload.phone = phone;
    }
    if (values.website) {
        // Champ piège : rempli uniquement par les robots.
        payload.website = values.website;
    }
    return payload;
}

// Champs du service -> champs du formulaire.
const SERVICE_FIELDS = {
    first_name: 'name',
    last_name: 'name',
    firstName: 'name',
    lastName: 'name',
    name: 'name',
    email: 'email',
    phone: 'phone',
    message: 'message'
};

/**
 * Rattache un message d'erreur du service à un champ du formulaire quand c'est
 * possible (ex. « Indiquez une adresse email valide. »).
 */
export function fieldForServerMessage(message) {
    const text = (message || '').toLowerCase();
    const quoted = text.match(/«\s*([a-z_]+)\s*»/i);
    if (quoted && SERVICE_FIELDS[quoted[1]]) {
        return SERVICE_FIELDS[quoted[1]];
    }
    if (text.includes('adresse email')) {
        return 'email';
    }
    if (text.startsWith('le message')) {
        return 'message';
    }
    return null;
}

/**
 * Lit les erreurs renvoyées par le service. Le service renvoie `{"detail": "..."}` ;
 * une liste d'erreurs par champ (`detail: [{loc, msg}]` ou `errors: {champ: msg}`)
 * est aussi prise en charge.
 */
export function parseServerErrors(body) {
    const fieldErrors = {};
    let message = null;
    if (!body || typeof body !== 'object') {
        return {message, fieldErrors};
    }
    const detail = body.detail;
    if (typeof detail === 'string') {
        const field = fieldForServerMessage(detail);
        if (field) {
            fieldErrors[field] = detail;
        } else {
            message = detail;
        }
    } else if (Array.isArray(detail)) {
        detail.forEach(item => {
            const loc = (item && Array.isArray(item.loc)) ? item.loc : [];
            const field = SERVICE_FIELDS[loc[loc.length - 1]];
            const text = item && item.msg ? String(item.msg) : null;
            if (field && text && !fieldErrors[field]) {
                fieldErrors[field] = text;
            } else if (text && !message) {
                message = text;
            }
        });
    }
    if (body.errors && typeof body.errors === 'object' && !Array.isArray(body.errors)) {
        Object.keys(body.errors).forEach(key => {
            const field = SERVICE_FIELDS[key];
            if (field && !fieldErrors[field]) {
                fieldErrors[field] = String(body.errors[key]);
            }
        });
    }
    if (!message && typeof body.message === 'string' && Object.keys(fieldErrors).length === 0) {
        message = body.message;
    }
    return {message, fieldErrors};
}

async function readJson(response) {
    try {
        return await response.json();
    } catch (e) {
        return null;
    }
}

/**
 * Envoie le formulaire et traduit la réponse en résultat affichable.
 *
 * Retourne `{ok: true, message}` en cas de succès, sinon
 * `{ok: false, kind, message, fieldErrors}` où `kind` vaut `validation`,
 * `captcha`, `rate_limit`, `unavailable`, `network` ou `server`.
 */
export async function submitContact(payload, options = {}) {
    const url = options.url || CONTACT_API_URL;
    const fetchImpl = options.fetch || window.fetch.bind(window);
    const timeoutMs = options.timeoutMs || REQUEST_TIMEOUT_MS;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    let response;
    try {
        response = await fetchImpl(url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
            body: JSON.stringify(payload),
            signal: controller ? controller.signal : undefined
        });
    } catch (e) {
        return {
            ok: false,
            kind: 'network',
            message: "Impossible de joindre le service d'envoi. Vérifiez votre connexion internet puis réessayez.",
            fieldErrors: {}
        };
    } finally {
        if (timer) {
            clearTimeout(timer);
        }
    }

    const body = await readJson(response);

    if (response.ok) {
        const message = body && typeof body.message === 'string' && body.message
            ? body.message
            : '';
        return {ok: true, message};
    }

    const {message, fieldErrors} = parseServerErrors(body);
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;

    switch (response.status) {
        case 400:
        case 422:
            if (hasFieldErrors) {
                return {
                    ok: false,
                    kind: 'validation',
                    message: message || 'Certains champs sont incorrects.',
                    fieldErrors
                };
            }
            // Les champs ont déjà été vérifiés : un refus sans champ désigné vient
            // de la vérification anti-spam.
            return {
                ok: false,
                kind: 'captcha',
                message: (message || 'La vérification anti-spam a refusé l\'envoi.')
                    + ' Réessayez dans quelques instants ou écrivez-nous à ' + CONTACT_EMAIL + '.',
                fieldErrors: {}
            };
        case 429:
            return {
                ok: false,
                kind: 'rate_limit',
                message: message || 'Trop de messages envoyés. Réessayez dans quelques minutes.',
                fieldErrors: {}
            };
        case 401:
        case 403:
            return {
                ok: false,
                kind: 'unavailable',
                message: "Le formulaire de contact n'est pas disponible pour le moment. Écrivez-nous à "
                    + CONTACT_EMAIL + '.',
                fieldErrors: {}
            };
        case 503:
            return {
                ok: false,
                kind: 'unavailable',
                message: (message || 'Le service est momentanément indisponible.')
                    + ' Réessayez dans quelques instants.',
                fieldErrors: {}
            };
        default:
            return {ok: false, kind: 'server', message: FALLBACK_ERROR, fieldErrors: {}};
    }
}
